import { useId, useState } from "react";
import { Link } from "react-router";

import { ConsignPanel } from "../consign/ConsignPanel.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { SHARE_CODES_COPY } from "./shareCodes.constants.ts";
import { useShareCodesController } from "./useShareCodesController.ts";
import type { ShareCodesCopy } from "./shareCodes.constants.ts";
import type { IssuableWorksReader } from "./useShareCodesController.ts";
import type { ShareCodeRow } from "./shareCodes.model.ts";
import "./shareCodes.css";

/**
 * Share codes, from the issuer's side — EITHER issuer's.
 *
 * The agent rail's "Share Codes / Agree a Consignment", both halves, and the
 * collector rail's "Share Codes", which is the first half only. A form to issue
 * a code, the list of what has been issued grouped open and ended, and — where
 * `copy.sale` is present — the second half beneath them: a work picker over the
 * same works with the consignment panel under it, which is how an agent starts
 * a sale, by sending the work to a gallery that can make one.
 *
 * THE SALE PANEL IS ABSENT FOR AN OWNER, and `copy.sale` being null is how it
 * says so. It said this was because "an owner selling their own work has no
 * door in the registry — `Capabilities.transferWork` stays false for them";
 * OND-173 built that door and the capability answers true for an owner now, so
 * the reason is a different one. A sale is about ONE WORK and lives on that
 * work's own record (`sell/OwnerSalePanel.tsx`); this screen is a ledger of
 * codes across all of them. The owner's `notHere` points at the door rather
 * than claiming there is none. See `OWNER_SHARE_CODES_COPY`.
 *
 * `canIssue` is the editor rung on the agent's side. `issue_share_code` refuses
 * a viewer at the firm, so the form is replaced by a sentence for them; the
 * list is theirs to read regardless, because knowing who the firm has shown a
 * work to is the least a member needs. On the collector's side there is no rung
 * and the caller passes a capability instead — see `CollectorWorkspace`.
 *
 * It carries no title of its own: the portal header above it does.
 */
export function ShareCodes({
  workBase,
  asOndoId,
  canIssue,
  cannotIssue,
  readIssuableWorks,
  copy = SHARE_CODES_COPY,
}: {
  workBase: string;
  asOndoId: string | null;
  canIssue: boolean;
  /** The sentence shown in the form's place when `canIssue` is false. */
  cannotIssue: string;
  /** Mandates for an agent, owned works for an owner. */
  readIssuableWorks: IssuableWorksReader;
  copy?: ShareCodesCopy;
}) {
  const c = useShareCodesController({ workBase, asOndoId, readIssuableWorks, copy });
  const { view } = c;

  if (view.kind === "loading") {
    return (
      <p className="sharecodes__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="sharecodes__note sharecodes__note--error" role="alert">
        <h2 className="sharecodes__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  return (
    <>
      <section className="sharecodes__lead">
        <p>
          <strong>{copy.lead}</strong> {copy.leadDetail}
        </p>
      </section>

      {canIssue ? (
        <IssueForm
          works={view.works}
          noWorks={view.noWorks}
          draft={c.draft}
          setDraft={c.setDraft}
          onIssue={c.issue}
          issuing={c.issuing}
          justIssued={c.justIssued}
          copy={copy}
        />
      ) : (
        <p className="sharecodes__note sharecodes__note--quiet">{cannotIssue}</p>
      )}

      {c.error && (
        <p className="sharecodes__note sharecodes__note--error" role="alert">
          {c.error}
        </p>
      )}

      {view.empty && (
        <EmptyState className="sharecodes__empty" lead={view.empty.message} detail={view.empty.detail} />
      )}

      {view.live.length > 0 && (
        <Group title={copy.groups.live} count={view.live.length}>
          {view.live.map((row) => (
            <CodeCard
              key={row.shareCodeId}
              row={row}
              copy={copy}
              confirming={c.confirmingId === row.shareCodeId}
              busy={c.working === row.shareCodeId}
              onAsk={() => c.askToEnd(row.shareCodeId)}
              onCancel={c.cancelEnd}
              onEnd={() => c.end(row.shareCodeId)}
            />
          ))}
        </Group>
      )}

      {view.ended.length > 0 && (
        <Group title={copy.groups.ended} count={view.ended.length}>
          {view.ended.map((row) => (
            <CodeCard
              key={row.shareCodeId}
              row={row}
              copy={copy}
              confirming={false}
              busy={false}
            />
          ))}
        </Group>
      )}

      {/* THE SECOND HALF OF THE LABEL, where the reader has one. A picker over
          the same works the issue form offers, and the consignment panel under
          it for the chosen one — the same component the work record mounts, so
          the two never drift. Absent, not disabled, for an owner: `copy.sale`
          is null there because there is no door, and the header's title is
          "Share Codes" alone rather than a half it cannot honour. */}
      {copy.sale && (
        <section className="sharecodes__sale">
          <h2 className="sharecodes__group-title">{copy.sale.title}</h2>
          <p className="sharecodes__note">
            <strong>{copy.sale.lead}</strong> {copy.sale.detail}
          </p>
          {canIssue ? (
            <SalePicker
              works={view.works}
              noWorks={view.noWorks}
              canAct={canIssue}
              copy={copy.sale}
            />
          ) : (
            <p className="sharecodes__note sharecodes__note--quiet">{cannotIssue}</p>
          )}
        </section>
      )}

      <section className="sharecodes__nothere">
        <h2 className="sharecodes__nothere-title">{copy.notHere.title}</h2>
        <ul className="sharecodes__nothere-list">
          {copy.notHere.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>
    </>
  );
}

function IssueForm({
  works,
  noWorks,
  draft,
  setDraft,
  onIssue,
  issuing,
  justIssued,
  copy: all,
}: {
  works: { workId: string; label: string }[];
  noWorks: string | null;
  draft: { workId: string; days: number; intendedFor: string; note: string };
  setDraft: (patch: Partial<{ workId: string; days: number; intendedFor: string; note: string }>) => void;
  onIssue: () => void;
  issuing: boolean;
  justIssued: string | null;
  copy: ShareCodesCopy;
}) {
  const workId = useId();
  const daysId = useId();
  const forId = useId();
  const noteId = useId();
  const [copied, setCopied] = useState(false);
  const copy = all.issue;

  return (
    <section className="sharecodes__issue">
      <h2 className="sharecodes__issue-title">{copy.title}</h2>

      {noWorks ? (
        <p className="sharecodes__note sharecodes__note--quiet">{noWorks}</p>
      ) : (
        <form
          className="sharecodes__form"
          onSubmit={(event) => {
            event.preventDefault();
            onIssue();
          }}
        >
          <label className="sharecodes__label" htmlFor={workId}>
            {copy.work}
          </label>
          <select
            id={workId}
            className="sharecodes__input"
            value={draft.workId}
            onChange={(event) => setDraft({ workId: event.target.value })}
            required
            disabled={issuing}
          >
            <option value="">{copy.workPlaceholder}</option>
            {works.map((work) => (
              <option key={work.workId} value={work.workId}>
                {work.label}
              </option>
            ))}
          </select>

          <label className="sharecodes__label" htmlFor={daysId}>
            {copy.days}
          </label>
          <div className="sharecodes__days">
            <input
              id={daysId}
              className="sharecodes__input sharecodes__input--days"
              type="number"
              min={1}
              max={30}
              value={draft.days}
              onChange={(event) => setDraft({ days: Number(event.target.value) })}
              disabled={issuing}
            />
            <span className="sharecodes__hint">{copy.daysNote}</span>
          </div>

          <label className="sharecodes__label" htmlFor={forId}>
            {copy.intendedFor}
          </label>
          <input
            id={forId}
            className="sharecodes__input"
            value={draft.intendedFor}
            onChange={(event) => setDraft({ intendedFor: event.target.value })}
            placeholder={copy.intendedForPlaceholder}
            maxLength={200}
            disabled={issuing}
          />

          <label className="sharecodes__label" htmlFor={noteId}>
            {copy.note}
          </label>
          <input
            id={noteId}
            className="sharecodes__input"
            value={draft.note}
            onChange={(event) => setDraft({ note: event.target.value })}
            placeholder={copy.notePlaceholder}
            maxLength={2000}
            disabled={issuing}
          />

          <button
            type="submit"
            className="sharecodes__submit"
            disabled={issuing || !draft.workId}
          >
            {issuing ? copy.issuing : copy.submit}
          </button>
        </form>
      )}

      {justIssued && (
        <p className="sharecodes__issued" role="status">
          {copy.issued}{" "}
          <code className="sharecodes__code sharecodes__code--new">{justIssued}</code>
          <button
            type="button"
            className="sharecodes__copy"
            onClick={() => {
              void navigator.clipboard
                .writeText(justIssued)
                .then(() => setCopied(true))
                .catch(() => undefined);
            }}
          >
            {copied ? copy.copied : copy.copy}
          </button>
        </p>
      )}
    </section>
  );
}

/** Only mounted where `copy.sale` is non-null, which is why it takes that. */
function SalePicker({
  works,
  noWorks,
  canAct,
  copy,
}: {
  works: { workId: string; label: string }[];
  noWorks: string | null;
  canAct: boolean;
  copy: NonNullable<ShareCodesCopy["sale"]>;
}) {
  const [workId, setWorkId] = useState("");
  const id = useId();

  if (noWorks) return <p className="sharecodes__note sharecodes__note--quiet">{noWorks}</p>;

  return (
    <div className="sharecodes__sale-body">
      <label className="sharecodes__label" htmlFor={id}>
        {copy.work}
      </label>
      <select
        id={id}
        className="sharecodes__input"
        value={workId}
        onChange={(event) => setWorkId(event.target.value)}
      >
        <option value="">{copy.workPlaceholder}</option>
        {works.map((work) => (
          <option key={work.workId} value={work.workId}>
            {work.label}
          </option>
        ))}
      </select>
      {workId && <ConsignPanel key={workId} workId={workId} canAct={canAct} />}
    </div>
  );
}

function Group({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="sharecodes__group">
      <h2 className="sharecodes__group-title">
        {title} ({count})
      </h2>
      <ul className="sharecodes__list">{children}</ul>
    </section>
  );
}

function CodeCard({
  row,
  copy,
  confirming,
  busy,
  onAsk,
  onCancel,
  onEnd,
}: {
  row: ShareCodeRow;
  copy: ShareCodesCopy;
  confirming: boolean;
  busy: boolean;
  onAsk?: () => void;
  onCancel?: () => void;
  onEnd?: () => void;
}) {
  return (
    <li className={`sharecodes__card${row.live ? "" : " sharecodes__card--ended"}`}>
      <div className="sharecodes__card-head">
        <code className="sharecodes__code">{row.code}</code>
        <span className="sharecodes__when">{row.when}</span>
      </div>
      <Link className="sharecodes__work" to={row.href}>
        {row.title}
        <span className="sharecodes__work-code">{row.ondoCode}</span>
      </Link>
      <p className="sharecodes__meta">
        {row.issuedBy}
        {row.intendedFor && <> · {row.intendedFor}</>}
      </p>
      {row.note && <p className="sharecodes__meta sharecodes__meta--note">{row.note}</p>}
      <p className="sharecodes__redeemed">
        {row.redeemedBy.length === 0 ? (
          copy.nobodyYet
        ) : (
          <>
            <span className="sharecodes__redeemed-label">{copy.redeemedBy}</span>{" "}
            {row.redeemedBy.join(", ")}
          </>
        )}
      </p>

      {row.live &&
        onAsk &&
        (confirming ? (
          <div className="sharecodes__end-confirm">
            <p className="sharecodes__end-note">{copy.endNote}</p>
            <button type="button" className="sharecodes__end-yes" disabled={busy} onClick={onEnd}>
              {copy.endConfirm}
            </button>
            <button type="button" className="sharecodes__end-no" onClick={onCancel}>
              {copy.endCancel}
            </button>
          </div>
        ) : (
          <button type="button" className="sharecodes__end" disabled={busy} onClick={onAsk}>
            {copy.end}
          </button>
        ))}
    </li>
  );
}
